
import { constants } from '../Localization';
import { convertToString, generatePluralStr } from '../util';
import { DEFAULT_PAGE_SIZE, localization, nonNumericOperators, numericOperators } from './constants';
import { CollectionPreferenceVisibleContentConfig, ColumnDefinition, ColumnTypes, 
    CustomPropertyFilterGroup, FilterProperty, Preference, VisibleContentOption 
} from './types';
/**
 * Build visible content config that is used in Polaris Collection Preference
 * @param options Visible content option group, each option should have an id and option lable
 * @param title The title of the preferences modal dialog
 * @param label The text to display as a title for the options group
 * @returns Visible content config
 */
export const buildVisibleContentOption = (
    options: VisibleContentOption[] | undefined,
    title = localization.VISIBLE_COLUMN_HEADER,
    label = localization.PREFERENCE_HEADER,
): CollectionPreferenceVisibleContentConfig => {
    return  {
        title,
        options: [
            {
                label,
                options: options || [],
            }
        ],
    };
};

/**
 * Build table prefence from the visible columns
 * @param visibleColumns visible columns
 * @param pageSize The default value is 10
 * @returns table preference
 */
export const buildTablePreference = (visibleColumns?: string[], pageSize = DEFAULT_PAGE_SIZE): Preference => {
    return {
        custom: 'table',
        pageSize,
        visibleContent: visibleColumns,
        wrapLines: false,
    };
};

function isTextBooleanColumn(type: ColumnTypes) {
    return type === ColumnTypes.TEXT || type === ColumnTypes.BOOLEAN;
}

/**
 * Build filter properties from column definitions
 * @param columnDefinitions column definitions
 * @returns An array of filter property
 */
export const buildTableFilterProperties = <T>(columnDefinitions: ColumnDefinition<T>[], customGroup?: CustomPropertyFilterGroup<T>): FilterProperty[] => {
    let filterProperties: FilterProperty[] = columnDefinitions.map(columnDef => {
        if (customGroup && customGroup.group === columnDef.id) { //don't include column in filter that has a separate custom group
            return null;
        }
        const operators = isTextBooleanColumn(columnDef.type) ? nonNumericOperators : numericOperators;

        return {
            propertyLabel: `${columnDef.header}` || columnDef.text || columnDef.id,
            key: columnDef.id,
            groupValuesLabel: `${columnDef.header} values`,
            operators
        };
    }).filter(filterProp => filterProp) as FilterProperty[];

    if (customGroup) {
        const customFilterProperties: FilterProperty[] = customGroup.propertyDefinitions.map(columnDef => {
            const operators = isTextBooleanColumn(columnDef.type) ? nonNumericOperators : numericOperators;

            return {
                propertyLabel: `${columnDef.header}` || columnDef.text || columnDef.id,
                key: columnDef.id,
                groupValuesLabel: `${columnDef.header} values`,
                group: customGroup.group,
                operators
            };
        });
        filterProperties = filterProperties.concat(customFilterProperties);
    }

    return filterProperties;
};

/**
 * Generate the filter result text
 * @param count matched count
 * @returns filter result text
 */
export const generateFilterCounterText = (count?: number): string => {
    return count ? `${count} ${count === 1 ? 'match' : 'matches'}` : '';
};

/**
 * Generate default visible contents from column definition
 * @param columnDefinitions 
 * @returns visible contents with all columns editable
 */
export const generateVisibleContents = <T>(columnDefinitions: ColumnDefinition<T>[]): VisibleContentOption[] => {
    return columnDefinitions.map(columnDef => {
        return {
            id: columnDef.id,
            label: columnDef.text || (columnDef.header)?.toString() || columnDef.id,
            editable: true,
        };
    });
};
/**
 * Returns the text for the records that will be changed, 
 *  if there are selected records, it returns a text for the number of selected records
 *  otherwise, if there are filtered records, it returns a text for the number of filtered records
 *  otherwise, if there are any records, it indicates 'ALL' and their count
 * @param selectionCount
 * @param filterCount
 * @param recordCount
 * @param resourceType
 * @returns 
 */
export const getCountText = (selectionCount: number, filterCount: number, recordCount: number, resourceType = ''): string => {
    let countText = '';
    let itemsCount = 0;
    if (selectionCount !== 0) {
        // user selection has first priority
        countText = `${selectionCount} ${constants.SELECTED}`;
        itemsCount = selectionCount;
    } else if (filterCount !== 0) {
        // filtered records have 2nd priority
        countText = `${filterCount} ${constants.FILTERED}`;
        itemsCount = filterCount;
    } else if (recordCount !== 0) {
        // all are impacted
        countText = `${constants.ALL_CAPITALIZED} (${recordCount})`;
        itemsCount = 2; // placeholder for 'ALL'
    }

    if (countText) {
        const resourceName = resourceType ? ` ${resourceType.toLowerCase()}` : '';
        countText = generatePluralStr(itemsCount, `${countText}${resourceName} ${constants.RECORD}`);
    }
    return countText;
};

/**
 * Format the given value based on the column type to use in filtering
 * @param value 
 * @param colType 
 * @returns 
 */
export const formatValueForFilter = (value: string | number, colType: ColumnTypes | undefined): string | number => {
    if (colType === ColumnTypes.BOOLEAN) { // makes boolean values True and true equal so filtering will be correct
        return convertToString(value).toLowerCase();
    } else if (colType === ColumnTypes.NUMBER) { // convert to number so comparisons work correctly
        // if numbers are percentages, need to remove %
        const updatedValue = convertToString(value).replace('%','');
        return Number(updatedValue);
    } else {
        return value;
    }
};
